import { requireSessionAccessToken } from '@/modules/auth/sessionToken';
import { authFetch } from '@/modules/auth/utils/authFetch';
import { apiFetchInit, apiUrl } from '@/modules/core/apiUrl';
import * as Sentry from '@sentry/browser';

// Simple result cache for matches (not in-progress requests)
const matchesResultCache = new Map();
const matchDetailsResultCache = new Map();
// In-flight request dedup: concurrent identical calls share one promise
const matchesInflight = new Map();
const matchDetailsInflight = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Cache key generator
function getCacheKey(userId, operation, id = '') {
  return `${operation}:${userId}:${id}`;
}

// Clear expired cache entries
function clearExpiredCache(cacheMap) {
  const now = Date.now();
  for (const [key, entry] of cacheMap.entries()) {
    if (now - entry.timestamp > CACHE_DURATION) {
      cacheMap.delete(key);
    }
  }
}

class MatchesAPI {
  async fetchMatches(userId) {
    clearExpiredCache(matchesResultCache);
    
    const cacheKey = getCacheKey(userId, 'fetchMatches');
    
    // Check for cached result (not in-progress request)
    if (matchesResultCache.has(cacheKey)) {
      const cached = matchesResultCache.get(cacheKey);
      console.log('Returning cached matches result');
      return cached.data;
    }

    console.log('Fetching matches for user:', userId);

    // Share the in-flight request so rapid successive calls (e.g. an
    // infinite render loop or concurrent mounts) make one network request
    let inflight = matchesInflight.get(cacheKey);
    if (!inflight) {
      inflight = this.performFetchMatches(userId);
      matchesInflight.set(cacheKey, inflight);
      // .then(success, failure) — not .finally — so the derived promise
      // resolves and never triggers unhandled-rejection warnings
      inflight.then(
        () => { if (matchesInflight.get(cacheKey) === inflight) matchesInflight.delete(cacheKey); },
        () => { if (matchesInflight.get(cacheKey) === inflight) matchesInflight.delete(cacheKey); }
      );
    }

    try {
      const result = await inflight;
      
      // Cache the successful result
      matchesResultCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    } catch (error) {
      throw error;
    }
  }

  async performFetchMatches(userId) {
    try {
      const token = await requireSessionAccessToken();

      const response = await authFetch('/api/matches', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const matches = await response.json();
      console.log('Fetched matches:', matches.length);
      return matches;
    } catch (error) {
      console.error('Error fetching matches:', error);
      Sentry.captureException(error, {
        extra: {
          userId,
          context: 'fetchMatches'
        }
      });
      throw error;
    }
  }

  async getMatchDetails(matchId) {
    if (!matchId) {
      throw new Error('Match ID is required');
    }

    clearExpiredCache(matchDetailsResultCache);
    
    const cacheKey = getCacheKey('', 'getMatchDetails', matchId);
    
    // Check for cached result (not in-progress request)
    if (matchDetailsResultCache.has(cacheKey)) {
      const cached = matchDetailsResultCache.get(cacheKey);
      // Only log cache hits in development to avoid console spam
      if (import.meta.env.VITE_PUBLIC_APP_ENV === 'development') {
        console.log('Returning cached match details for ID:', matchId);
      }
      return cached.data;
    }

    console.log('Fetching match details from database for ID:', matchId);

    // Share the in-flight request so rapid successive calls (e.g. an
    // infinite render loop or concurrent mounts) make one network request
    let inflight = matchDetailsInflight.get(cacheKey);
    if (!inflight) {
      inflight = this.performGetMatchDetails(matchId);
      matchDetailsInflight.set(cacheKey, inflight);
      // .then(success, failure) — not .finally — so the derived promise
      // resolves and never triggers unhandled-rejection warnings
      inflight.then(
        () => { if (matchDetailsInflight.get(cacheKey) === inflight) matchDetailsInflight.delete(cacheKey); },
        () => { if (matchDetailsInflight.get(cacheKey) === inflight) matchDetailsInflight.delete(cacheKey); }
      );
    }

    try {
      const result = await inflight;
      
      // Cache the successful result
      matchDetailsResultCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      
      console.log('Successfully fetched and cached match details for ID:', matchId);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async performGetMatchDetails(matchId) {
    try {
      const token = await requireSessionAccessToken();

      const response = await authFetch('/api/match-details', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          matchId
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Match not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const matchDetail = await response.json();
      console.log('Fetched match details from database:', matchDetail);
      return matchDetail;
    } catch (error) {
      console.error('Error fetching match details:', error);
      Sentry.captureException(error, {
        extra: {
          matchId,
          context: 'getMatchDetails'
        }
      });
      throw error;
    }
  }

  async saveMatch(matchData, playerData) {
    try {
      const token = await requireSessionAccessToken();

      console.log('Saving match data:', { matchData, playerCount: playerData.length });

      const response = await fetch(apiUrl('/api/matches'), apiFetchInit({
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'saveMatch',
          matchData,
          playerData
        }),
      }));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Clear caches to ensure fresh data on next fetch
      this.clearCache();
      
      return result;
    } catch (error) {
      console.error('Error saving match:', error);
      Sentry.captureException(error, {
        extra: {
          matchData,
          playerCount: playerData?.length,
          context: 'saveMatch'
        }
      });
      throw error;
    }
  }

  clearCache() {
    matchesResultCache.clear();
    matchDetailsResultCache.clear();
    matchesInflight.clear();
    matchDetailsInflight.clear();
    console.log('Matches cache cleared');
  }
}

export const matchesApi = new MatchesAPI();

/**
 * React hook for managing matches data
 * @returns {Object} Object containing match-related functions
 */
export function useMatches() {
  return {
    fetchMatches: matchesApi.fetchMatches.bind(matchesApi),
    getMatchDetails: matchesApi.getMatchDetails.bind(matchesApi),
    saveMatch: matchesApi.saveMatch.bind(matchesApi),
    clearCache: matchesApi.clearCache.bind(matchesApi)
  };
}